import handleQueryResult from "./util/handleQueryResult"
import type { SurveyDefinitionKey } from "mco-keys/src/t4/SurveyDefinitionKey"
import type { PatientConditionIssueFixKey } from "../../components/patients/conditions/investigator/PatientConditionIssueFixes"
import type CommunicationTypeKey from "mco-keys/src/t4/CommunicationTypeKey"
import type { PatientCondition } from "../../models/PatientCondition"
import type { AxiosInstance } from "axios"

export type PatientConditionEvent = {
	id: number
	userName: string
	issueKeys: string[]
	fixKey: PatientConditionIssueFixKey
	fixKeys: PatientConditionIssueFixKey[]
	date: string
	codeName: string
	eventTypeName: string
	preOrPostOp: string
	benchmarkName: string
	isCro: string
	status: string
	assessmentType: string
	surveys: SurveyDefinitionKey[]
	events: PatientConditionEvent[]
	emailDescription: string
	procedureName: string
	to: string
	body: string
}

export type PatientCommunicationEvent = PatientConditionEvent & {
	to: string
	templateKey: string
	templateName: string
	communicationTypeKey: CommunicationTypeKey
}

type PatientConditionEventGroup = {
	date: string
	events: PatientConditionEvent[]
}

export type PatientConditionInvestigationModel = {
	details: PatientCondition
	patientConditionId: number
	userId: string
	firstName: string
	lastName: string
	hospital: string
	condition: string
	events: PatientConditionEventGroup[]
}

export type PatientConditionIssuesSummary = {
	patientConditionId: number
	userName: string
	hospital: string
	firstname: string
	lastname: string
	issueKeys: string[]
}

class PatientConditionIssuesClient {
	constructor(private axiosClient: AxiosInstance) {}

	public getFilters = () =>
		handleQueryResult(
			this.axiosClient.get<Record<string, string>>(
				`/api/admin/investigations/filters`
			)
		)

	public getAllSummary = ({
		filter,
		includeTestHospitals
	}: {
		filter: string
		includeTestHospitals?: boolean
	}) =>
		handleQueryResult(
			this.axiosClient.get<PatientConditionIssuesSummary[]>(
				`/api/admin/investigations?includeTestHospital=${!!includeTestHospitals}&filterByIssue=${filter}`
			)
		)

	public get = (patientConditonId: number) =>
		handleQueryResult(
			this.axiosClient.get<PatientConditionInvestigationModel>(
				`/api/admin/investigations/${patientConditonId}`
			)
		)

	public markAsOk = (patientConditionId: number) =>
		handleQueryResult(
			this.axiosClient.post<void>(
				`/api/admin/investigations/${patientConditionId}/markOk`
			)
		)

	public fixAssessmentIssue = ({
		patientConditionId,
		eventId,
		selectedFix
	}: {
		patientConditionId: number
		eventId: number
		selectedFix: string
	}) =>
		handleQueryResult(
			this.axiosClient.post<void>(
				`/api/admin/investigations/${patientConditionId}/${eventId}/${selectedFix}`
			)
		)
}

export default PatientConditionIssuesClient
